import { http } from "@/shared/lib/api/http"
import {
  type BranchSummary,
  type CreateOfferingTypeInput,
  type OfferingType,
  type OfferingTypesListQuery,
  type OfferingTypesListResponse,
  type UpdateOfferingTypeInput,
  type OfferingTypeStatus,
} from "@/features/church/config/offering-types/types/offering-type.types"

type OfferingTypeApiResponse = {
  id: string
  workspaceId?: string
  name: string
  description: string | null
  scope: OfferingType["scope"]
  ownerBranchId?: string | null
  kind: OfferingType["kind"]
  startDate?: string | null
  endDate?: string | null
  status: OfferingType["status"]
  ownerBranch?: BranchSummary | null
  createdAt?: string
  updatedAt?: string
}

type OfferingTypeListApiResponse = {
  items?: OfferingTypeApiResponse[]
  total?: number
  page?: number
  limit?: number
} | OfferingTypeApiResponse[]

type CreateOfferingTypeBody = {
  workspaceId: string
  name: string
  description?: string | null
  scope: OfferingType["scope"]
  ownerBranchId?: string | null
  status?: OfferingTypeStatus
}

type UpdateOfferingTypeBody = {
  name?: string
  description?: string | null
  scope?: OfferingType["scope"]
  ownerBranchId?: string | null
  status?: OfferingTypeStatus
}

const OFFERING_TYPES_MAX_LIMIT = 100

function normalizeOfferingType(input: OfferingTypeApiResponse): OfferingType {
  return {
    id: input.id,
    workspaceId: input.workspaceId ?? null,
    name: input.name,
    description: input.description,
    scope: input.scope,
    ownerBranchId: input.ownerBranchId ?? null,
    status: input.status,
    kind: input.kind,
    linkedProgramId: null,
    startDate: input.startDate ?? null,
    endDate: input.endDate ?? null,
    ownerBranch: input.ownerBranch ?? null,
    createdAt: input.createdAt ?? new Date().toISOString(),
    updatedAt: input.updatedAt ?? new Date().toISOString(),
  }
}

function normalizeListResponse(input: OfferingTypeListApiResponse): OfferingTypesListResponse {
  if (Array.isArray(input)) {
    return {
      items: input.map(normalizeOfferingType),
      total: input.length,
      page: 1,
      limit: input.length,
    }
  }

  const items = Array.isArray(input.items) ? input.items.map(normalizeOfferingType) : []
  return {
    items,
    total: typeof input.total === "number" ? input.total : items.length,
    page: typeof input.page === "number" && input.page > 0 ? input.page : 1,
    limit: typeof input.limit === "number" && input.limit > 0 ? input.limit : Math.max(items.length, 1),
  }
}

export async function listOfferingTypesService(
  query: OfferingTypesListQuery
): Promise<OfferingTypesListResponse> {
  const params: Record<string, string | number> = {
    workspaceId: query.workspaceId,
  }

  if (query.scope) params.scope = query.scope
  if (query.status) params.status = query.status
  if (query.ownerBranchId) params.ownerBranchId = query.ownerBranchId
  if (query.q && query.q.trim()) params.q = query.q.trim()
  if (typeof query.page === "number" && query.page > 0) params.page = query.page
  if (typeof query.limit === "number" && query.limit > 0) {
    params.limit = Math.min(query.limit, OFFERING_TYPES_MAX_LIMIT)
  }

  const response = await http.get<OfferingTypeListApiResponse>("/finance/offering-types", {
    params,
  })

  return normalizeListResponse(response.data)
}

export async function getOfferingTypeService(id: string): Promise<OfferingType> {
  const response = await http.get<OfferingTypeApiResponse>(`/finance/offering-types/${id}`)
  return normalizeOfferingType(response.data)
}

export async function createOfferingTypeService(
  body: CreateOfferingTypeBody
): Promise<OfferingType> {
  const payload: Record<string, unknown> = {
    workspaceId: body.workspaceId,
    name: body.name,
    description: body.description ?? null,
    scope: body.scope,
    ownerBranchId: body.ownerBranchId ?? null,
  }

  if (body.status !== undefined) {
    payload.status = body.status
  }

  const response = await http.post<OfferingTypeApiResponse>("/finance/offering-types", payload)
  return normalizeOfferingType(response.data)
}

export async function updateOfferingTypeService(
  id: string,
  body: UpdateOfferingTypeBody
): Promise<OfferingType> {
  const payload: Record<string, unknown> = {}

  if (body.name !== undefined) payload.name = body.name
  if (body.description !== undefined) payload.description = body.description ?? null
  if (body.scope !== undefined) payload.scope = body.scope
  if (body.ownerBranchId !== undefined) payload.ownerBranchId = body.ownerBranchId ?? null
  if (body.status !== undefined) payload.status = body.status

  const response = await http.patch<OfferingTypeApiResponse>(`/finance/offering-types/${id}`, payload)
  return normalizeOfferingType(response.data)
}

export async function updateOfferingTypeStatusService(
  id: string,
  status: OfferingTypeStatus
): Promise<OfferingType> {
  const response = await http.patch<OfferingTypeApiResponse>(`/finance/offering-types/${id}/status`, {
    status,
  })
  return normalizeOfferingType(response.data)
}

export type OfferingTypeCreateInput = CreateOfferingTypeInput & { workspaceId: string }
export type OfferingTypeUpdateInput = UpdateOfferingTypeInput
